import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useHistory } from 'react-router-dom';

import { Alert, Loading } from '../../components';
import { Badge, Button, Input } from '../../components/ui';
import { Window } from '../../contexts';
import { ProductSubmissionModal } from './ProductSubmissionModal';

import './store-catalog-panel.scss';

/**
 * Owner Admin → My Products (store-catalog feature).
 *
 * The store's OWN catalogue entries — brands, models and products the store
 * submitted on top of the shared Retail Circle catalogue. Rows are private to
 * this store and, unless RC has auto-approve on, sit as "Pending" until
 * Retail Circle approves them (only approved entries appear in pickers,
 * search and listings). Rejected rows stay here with the reason so the owner
 * can fix and resubmit (delete + add again).
 *
 * Adds happen through the discrete forms below (creation is a transactional
 * command, not field persistence — the no-Save-buttons rule doesn't apply);
 * the full product form lives on /add-new-product and is linked, not
 * duplicated.
 */

const STATUS_TONE = {
  approved: 'success',
  pending: 'warning',
  rejected: 'danger',
};

function StatusBadge({ status }) {
  const label = status ? status.charAt(0).toUpperCase() + status.slice(1) : '—';
  return <Badge tone={STATUS_TONE[status] || 'muted'}>{label}</Badge>;
}

function watchesCategoryId() {
  return (Window.CATEGORIES || []).find((c) => c.url_name === 'watches')?.id;
}

export function StoreCatalogPanel() {
  const history = useHistory();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const [newBrand, setNewBrand] = useState('');
  const [newModelBrandId, setNewModelBrandId] = useState('');
  const [newModel, setNewModel] = useState('');
  const [brandOptions, setBrandOptions] = useState([]);
  const [openProductId, setOpenProductId] = useState(null);

  const categoryId = watchesCategoryId();

  const load = async () => {
    setError('');
    try {
      const res = await axios.get('/api/store-catalog/entries');
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not load your catalogue entries.');
    } finally {
      setLoading(false);
    }
  };

  const loadBrandOptions = async () => {
    if (!categoryId) return;
    try {
      // usable=true: shared RC brands + THIS store's own brands in any status,
      // so a model can be submitted under a brand that's still pending
      // approval (the normal pickers stay approved-only).
      const res = await axios.get(`/api/categories/${categoryId}/brands?all=true&usable=true`);
      setBrandOptions(res.data || []);
    } catch (e) {
      setBrandOptions([]);
    }
  };

  // Mount-only fetch, same pattern as the sibling panels.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); loadBrandOptions(); }, []);

  const run = async (fn, fallbackMessage) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      await Promise.all([load(), loadBrandOptions()]);
    } catch (e) {
      const payload = e?.response?.data;
      setError(
        payload?.message
          || Object.values(payload?.errors || {}).flat().join(' ')
          || fallbackMessage
      );
    } finally {
      setBusy(false);
    }
  };

  // for_store: this is the STORE-facing surface, so an RC master acting as a
  // store submits FOR that store (into the approval queue) rather than
  // publishing to the shared catalogue — which is what happened before, and
  // then the new row vanished from this very list. No effect for store
  // admins (already forced to their own store) or for a master with no store
  // selected. Shared entries are added from Retail Circle Admin → Shared
  // Catalog, which deliberately omits this flag.
  const addBrand = () => {
    const name = newBrand.trim();
    if (!name || !categoryId) return;
    run(async () => {
      await axios.post('/api/brands', { category_id: categoryId, name, for_store: true });
      setNewBrand('');
    }, 'Could not add the brand.');
  };

  const addModel = () => {
    const name = newModel.trim();
    if (!name || !newModelBrandId) return;
    run(async () => {
      await axios.post('/api/models', { brand_id: Number(newModelBrandId), name, for_store: true });
      setNewModel('');
    }, 'Could not add the model.');
  };

  const remove = (type, row) => {
    const label = row.name || `${row.brand || ''} ${row.model || ''} ${row.reference_number || ''}`.trim();
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;
    run(
      () => axios.delete(`/api/store-catalog/${type}/${row.id}`),
      'Could not delete this entry.'
    );
  };

  if (loading) return <Loading />;
  if (error && !data) return <Alert type="error" message={error} />;
  if (!data) return null;

  const approvalNote = data.auto_approve
    ? 'New entries go live for your store immediately.'
    : 'New entries are reviewed by Retail Circle and appear in pickers, search and listings once approved.';

  return (
    <div className="scp">
      <p className="scp__intro">
        Your store&apos;s own brands, models and products — shown to your team
        alongside the shared Retail Circle catalogue, and never to other
        stores. {approvalNote}
      </p>

      <p className="scp__intro scp__intro--muted">
        Entries added here belong to this store and go through the approval
        queue. To add to the shared Retail Circle catalogue instead, use
        Retail Circle Admin → Shared Product Database.
      </p>

      {error && <Alert type="error" message={error} />}

      <div className="scp__adders">
        <div className="scp__adder">
          <span className="scp__adder-label">Add a brand</span>
          <div className="scp__adder-row">
            <Input
              size="md"
              placeholder="Brand name"
              value={newBrand}
              onChange={(e) => setNewBrand(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addBrand()}
            />
            <Button size="md" variant="secondary" disabled={busy || !newBrand.trim()} onClick={addBrand}>
              Add brand
            </Button>
          </div>
        </div>

        <div className="scp__adder">
          <span className="scp__adder-label">Add a model</span>
          <div className="scp__adder-row">
            <select
              value={newModelBrandId}
              onChange={(e) => setNewModelBrandId(e.target.value)}
              aria-label="Brand for the new model"
            >
              <option value="">Select brand…</option>
              {brandOptions.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                  {b.approval_status && b.approval_status !== 'approved'
                    ? ` (${b.approval_status})`
                    : ''}
                </option>
              ))}
            </select>
            <Input
              size="md"
              placeholder="Model name"
              value={newModel}
              onChange={(e) => setNewModel(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addModel()}
            />
            <Button size="md" variant="secondary" disabled={busy || !newModel.trim() || !newModelBrandId} onClick={addModel}>
              Add model
            </Button>
          </div>
        </div>

        <div className="scp__adder">
          <span className="scp__adder-label">Add a product</span>
          <div className="scp__adder-row">
            {/* ?for_store=1 carries the same intent as the flag above through
                the standalone product form route. */}
            <Button size="md" variant="primary" onClick={() => history.push('/add-new-product?for_store=1')}>
              Open the product form
            </Button>
          </div>
        </div>
      </div>

      <Group
        title="Products"
        rows={data.products}
        empty="No products submitted yet."
        columns={(row) => (
          <>
            <span className="scp__cell scp__cell--main">
              {row.brand} {row.model}
              {row.reference_number ? ` · ${row.reference_number}` : ''}
            </span>
            <span className="scp__cell scp__cell--muted">
              {row.images?.length ? `${row.images.length} image${row.images.length > 1 ? 's' : ''}` : 'No images'}
            </span>
          </>
        )}
        onOpen={(row) => setOpenProductId(row.id)}
        openLabel={(row) => (row.approval_status === 'approved' ? 'View' : 'View / Edit')}
        onDelete={(row) => remove('products', row)}
        busy={busy}
      />

      {openProductId !== null && (
        <ProductSubmissionModal
          productId={openProductId}
          categoryId={categoryId}
          onClose={() => setOpenProductId(null)}
          onChanged={() => load()}
        />
      )}

      <Group
        title="Models"
        rows={data.models}
        empty="No models submitted yet."
        columns={(row) => (
          <span className="scp__cell scp__cell--main">{row.brand} {row.name}</span>
        )}
        onDelete={(row) => remove('models', row)}
        busy={busy}
      />

      <Group
        title="Brands"
        rows={data.brands}
        empty="No brands submitted yet."
        columns={(row) => (
          <span className="scp__cell scp__cell--main">{row.name}</span>
        )}
        onDelete={(row) => remove('brands', row)}
        busy={busy}
      />
    </div>
  );
}

function Group({ title, rows, empty, columns, onOpen, openLabel, onDelete, busy }) {
  return (
    <div className="scp__group">
      <h4 className="scp__group-title">{title}</h4>
      {(!rows || rows.length === 0) ? (
        <p className="scp__empty">{empty}</p>
      ) : (
        <ul className="scp__list">
          {rows.map((row) => (
            <li key={`${title}-${row.id}`} className="scp__row">
              {columns(row)}
              <span className="scp__cell scp__cell--status">
                <StatusBadge status={row.approval_status} />
                {row.approval_status === 'rejected' && row.rejection_reason && (
                  <span className="scp__reason" title={row.rejection_reason}>
                    {row.rejection_reason}
                  </span>
                )}
              </span>
              <span className="scp__cell scp__cell--actions">
                {onOpen && (
                  <Button size="sm" variant="secondary" onClick={() => onOpen(row)}>
                    {openLabel ? openLabel(row) : 'View'}
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="danger"
                  disabled={busy || row.in_use}
                  title={row.in_use ? 'In use by listings, models or products — remove those first' : undefined}
                  onClick={() => onDelete(row)}
                >
                  Delete
                </Button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default StoreCatalogPanel;
